import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { PublicShell } from "../components/PublicShell";
import { Button } from "../components/ui/button";
import { Gauge, Fuel, Cog, Tag, CheckCircle2, CreditCard, ArrowRight, Loader2, Phone } from "lucide-react";
import { WakzyCheckPanel } from "../components/WakzyCheckPanel";
import { toast } from "sonner";

export default function PublicVehiclePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [idx, setIdx] = useState(0);
  const [reserving, setReserving] = useState(false);

  useEffect(() => { api.get(`/vehicles/${id}`).then((r) => setVehicle(r.data)); }, [id]);

  const reserveWithDeposit = async () => {
    setReserving(true);
    try {
      const { data } = await api.post("/payments/checkout", { vehicle_id: id, origin_url: window.location.origin });
      if (data.url) window.location.href = data.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start checkout"); setReserving(false); }
  };

  if (!vehicle) return <PublicShell><div className="pt-32 max-w-7xl mx-auto px-6 text-slate-400">Loading…</div></PublicShell>;
  const images = vehicle.images?.length ? vehicle.images : ["https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=1600"];

  return (
    <PublicShell>
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pt-28 pb-16">
        <Link to="/inventory" className="text-sm text-slate-400 hover:text-white mb-6 inline-block">← Back to inventory</Link>
        <div className="grid lg:grid-cols-2 gap-10 mt-4">
          <div>
            <div className="aspect-[16/10] rounded-md surface overflow-hidden bg-cover bg-center" style={{ backgroundImage: `url(${images[idx]})` }} data-testid="public-veh-image" />
            {images.length > 1 && (
              <div className="flex gap-2 mt-3">
                {images.map((img, i) => (
                  <button key={i} onClick={() => setIdx(i)} className={`w-24 h-16 bg-cover bg-center rounded-sm border-2 ${i === idx ? "border-[#D4AF37]" : "border-white/10"}`} style={{ backgroundImage: `url(${img})` }} />
                ))}
              </div>
            )}
          </div>
          <div className="fade-up">
            <div className="flex items-center gap-3 mb-3">
              <span className="pill pill-available">{vehicle.status}</span>
              <span className="label-eyebrow">{vehicle.year} · {vehicle.color}</span>
            </div>
            <h1 className="font-heading text-4xl lg:text-5xl font-light mb-2 chrome-text inline-block">{vehicle.make}</h1>
            <h2 className="font-heading text-2xl text-slate-300 mb-4">{vehicle.model}</h2>
            <div className="mb-6">
              <span className="chrome-nameplate"><span className="dot" />{vehicle.body_type} · {vehicle.transmission}<span className="dot" /></span>
            </div>
            <div className="flex items-baseline gap-3 mb-8">
              <span className="font-display text-6xl gold-text">${vehicle.price.toLocaleString()}</span>
              <span className="text-slate-400 text-sm">Sale price</span>
            </div>
            <p className="text-slate-300 leading-relaxed mb-8">{vehicle.description}</p>

            <div className="grid grid-cols-2 gap-3 mb-8">
              {[
                { icon: Gauge, label: "Mileage", value: `${vehicle.mileage?.toLocaleString()} mi` },
                { icon: Fuel, label: "Fuel", value: vehicle.fuel_type },
                { icon: Cog, label: "Transmission", value: vehicle.transmission },
                { icon: Tag, label: "VIN", value: vehicle.vin, mono: true },
              ].map((s, i) => (
                <div key={i} className="surface p-4 rounded-sm">
                  <s.icon className="w-4 h-4 text-[#D4AF37] mb-2" />
                  <div className="label-eyebrow mb-1">{s.label}</div>
                  <div className={`text-sm ${s.mono ? "font-mono text-xs" : ""}`}>{s.value}</div>
                </div>
              ))}
            </div>

            {vehicle.features?.length > 0 && (
              <div className="mb-10">
                <div className="gold-divider" />
                <div className="label-eyebrow mb-3 mt-4" style={{ color: "var(--wakzy-gold)" }}>Highlight features</div>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {vehicle.features.map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-300"><CheckCircle2 className="w-4 h-4 text-[#D4AF37]" /> {f}</li>
                  ))}
                </ul>
                <div className="gold-divider" />
              </div>
            )}

            <WakzyCheckPanel vehicle={vehicle} />

            <div className="flex flex-wrap gap-3">
              <Button size="lg" onClick={reserveWithDeposit} disabled={reserving || vehicle.status !== "available"} className="btn-cta rounded-sm h-14 px-8 text-base" data-testid="public-reserve-btn">
                {reserving ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <CreditCard className="w-5 h-5 mr-2" />}
                Reserve · $500 deposit
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate(`/financing?vehicle=${id}`)} className="rounded-sm h-14 px-8 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 bg-transparent">
                Apply Financing
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate(`/contact?vehicle=${id}`)} className="rounded-sm h-14 px-8 border-white/20 text-white bg-transparent hover:bg-white/5" data-testid="public-contact-btn">
                <Phone className="w-4 h-4 mr-2" /> Contact Sales
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-4">Or visit the dealership and use our paperless <Link to="/kiosk" className="text-[#D4AF37] hover:underline">tablet kiosk</Link> to complete your purchase.</p>
          </div>
        </div>
      </section>
    </PublicShell>
  );
}
